import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ApDynComponentComponent}                                from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {Observable, Subscription}                               from 'rxjs';
import {
  ApDynGridPagerConfigBase
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {IPpPlanningBookingGridItem}                             from '../types/pp-planning-booking.types';
import {
  DropDownButtonData
}                                                               from '../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {ApUtilService}                                          from '../../ap-utils/service/ap-util.service';
import {PpPlanningBookingGridConfig}                            from '../configs/pp-planning-booking-grid.config';
import {PpPlanningBookingDataService}                           from '../services/pp-planning-booking-data.service';
import {
  PpPlanningWizardComponent
}                                                               from '../../entry-components/plant-protection-entry-components/planning-wizard/pp-planning-wizard.component';
import {FormStore}                                              from '../../stores/layout/form.store';
import {RouterStore}                                            from '../../stores/router/router.store';
import {WorkTypes}                                              from '../../stores/base-data/worrktypes.store';
import {ModalDialogStore}                                       from '../../stores/dialog/modal.dialog.store';
import {ModalDialogButtonKeys, ModalDialogPresets}              from '../../stores/dialog/modal.dialog.presets';
import {
  IModalDialogResult
}                                                               from '../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {ApGuidUtil}                                             from '../../ap-utils';
import {
  PpPlanShowComponent
}                                                               from '../../entry-components/plant-protection-entry-components/pp-plan-show.component';
import {
  PpPlanBookingComponent
}                                                               from '../../entry-components/plant-protection-entry-components/pp-plan-booking.component';
import {OperationModesEnum}                                     from '../../stores/common/operation-modes.store';
import {
  PlantProtectionPlanBookStore
}                                                               from '../../stores/plant-protection/plant-protection-planbook.store';
import {SensorStore}                                            from '../../stores/sensor/sensor.store';
import {MapStore}                                               from '../../stores/map/map.store';

@Component({
  selector: 'plant-protection-planning',
  template: `
    <ap-dyncomponent [caption]="'Global__Plannings'"
                     [selectable]="{checkboxOnly: false, mode: 'single'}"
                     [items]="items$ | async"
                     [loading$]="loading$"
                     [headerIcon]="'ap-fungidetect-icon'"
                     [pager]="pager"
                     [columns]="columns"
                     [canSearch]="true"
                     [canCreate]="true"
                     (create)="onCreate()">
    </ap-dyncomponent>
  `,
  styles: []
})
export class PpPlanningComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ApDynComponentComponent) dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public items$: Observable<IPpPlanningBookingGridItem[]>;
  public loading$: Observable<boolean>;
  public pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = [];

  constructor(private dataService: PpPlanningBookingDataService,
              private formStore: FormStore,
              private sensorStore: SensorStore,
              private modalDialogStore: ModalDialogStore,
              private modalDialogPresets: ModalDialogPresets,
              private routerStore: RouterStore,
              private mapStore: MapStore,
              private plantProtectionPlanBookStore: PlantProtectionPlanBookStore,
              private gridConfig: PpPlanningBookingGridConfig) {
  }

  public ngOnInit(): void {
    this.dataService.init(false);
    this.loading$ = this.dataService.getLoading$();
    this.items$ = this.dataService.buildDataItems();
    this.dataService.subscribeLoad();
    this._buildGrid();
  }

  public ngAfterViewInit(): void {
    this.dataService.subscribeSelection(this.dynComponent);
  }

  public ngOnDestroy(): void {
    this.dataService.destroy();
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  private _buildGrid(): void {
    const dropDownActions: DropDownButtonData[] = [
      {
        text: 'Global__Show',
        callback: ApUtilService.createEventEmitter(this._onShow.bind(this), this._subscriptions)
      },
      {
        text: 'Task_Mgmt_Export',
        callback: ApUtilService.createEventEmitter(this._onExport.bind(this), this._subscriptions),
        disabled: (item: IPpPlanningBookingGridItem) => {
          return item.PlanBooks?.FirstOrDefault()?.ProductList?.FirstOrDefault()?.ProductListDetails?.WorkType !== WorkTypes.Spraying;
        }
      },
      {
        text: 'Global__Booking',
        callback: ApUtilService.createEventEmitter(this._onBook.bind(this), this._subscriptions)
      },
      {
        text: 'Global__Delete',
        callback: ApUtilService.createEventEmitter(this._onDelete.bind(this), this._subscriptions)
      }
    ];

    this.columns = this.gridConfig.getGridColumns(dropDownActions);
    const pagerOptions = [
      {
        text: 'Global__Delete',
        callback: ApUtilService.createEventEmitter(this._onDeleteMany.bind(this), this._subscriptions)
      },
    ];
    this.pager = this.gridConfig.getGridPager(pagerOptions);
  }

  /**
   * Opens the pp planning wizard.
   * The preselection is handled in dynForm's selection change already
   */
  public onCreate(): void {
    this.mapStore.Layers.PpApplicationMapLayer.clear();
    this.formStore.openForm({component: PpPlanningWizardComponent, data: null});
  }

  private _onShow(dataItem: IPpPlanningBookingGridItem): void {
    this.formStore.openForm({component: PpPlanShowComponent, data: {DataItem: dataItem, IsBooked: false}});
  }

  private _onExport(dataItem: any): void {
    this.routerStore.navigate(['task-management/overview'], {
      id: dataItem.Id,
      url: 'plant-protection/planning'
    }).then();
  }

  private _onBook(dataItem: IPpPlanningBookingGridItem): void {
    if (!dataItem.PlanBooks || dataItem.PlanBooks.length <= 0) {
      return;
    }
    this.formStore.openForm({
      component: PpPlanBookingComponent,
      data: {
        DataItem: dataItem.PlanBooks,
        IsBooked: false
      }
    });
  }

  private _onDelete(dataItem: IPpPlanningBookingGridItem): void {
    this.modalDialogStore.setModalDialogData(this.modalDialogPresets.DeleteDialog({
      resultDelegate: (dialogResult: IModalDialogResult): void => {
        if (dialogResult?.key !== ModalDialogButtonKeys.Delete ||
          dataItem?.Id == null ||
          dataItem?.Id === ApGuidUtil.getEmptyGuid()) {
          return;
        }
        this.plantProtectionPlanBookStore.deleteSummaries([dataItem?.Id]);
      }
    }));
  }

  private _onDeleteMany(): void {
    this.plantProtectionPlanBookStore.deleteSummaries(
      this.dynComponent.getSelectedItems().map((item: IPpPlanningBookingGridItem) => item?.Id));
  }
}
